import { useCallback, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import axios from 'axios'
import { useToast } from '../utils/hooks'
import { useFetcher } from '../utils/fetcher'
import UploadProgress from './UploadProgress'
import Modal from './Modal'
import { BucketName, parseSize } from '@bm-js/atlascloud-shared'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

interface FileDropzoneProps {
  onUploadSuccess: (uuids: string[]) => Promise<void>
  onUploadError?: (error: Error) => void
  allowAnyFileType?: boolean
  maxFileSizeOverride?: number
  /**
   * @default BucketName.ATLASCLOUD_FILES
   */
  bucket?: BucketName
}

interface UploadProgressType {
  name: string
  percentage: number
}

const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200MB

const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
} as const

const ACCEPTED_VIDEO_TYPES = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
} as const

const ACCEPTED_DOCUMENT_TYPES = {
  'application/pdf': ['.pdf'],
} as const

// Combine all accepted types for dropzone
const ACCEPTED_TYPES = {
  ...ACCEPTED_IMAGE_TYPES,
  ...ACCEPTED_VIDEO_TYPES,
  ...ACCEPTED_DOCUMENT_TYPES,
}

const FileDropzone = ({
  onUploadSuccess,
  onUploadError,
  allowAnyFileType,
  maxFileSizeOverride,
  bucket = BucketName.ATLASCLOUD_FILES,
}: FileDropzoneProps) => {
  const { tr } = useTr()
  const [progress, setProgress] = useState<UploadProgressType[]>([])
  const [status, setStatus] = useState<'idle' | 'uploading' | 'error'>('idle')
  const { success, error: showError } = useToast()
  const fetcher = useFetcher()

  const maxFileSize = maxFileSizeOverride || MAX_FILE_SIZE
  const accept = allowAnyFileType ? undefined : ACCEPTED_TYPES

  const uploadToS3 = async (uploadUrl: string, file: File) => {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        const percentage = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 100)
        )
        setProgress((prev) =>
          prev.map((p) => (p.name === file.name ? { ...p, percentage } : p))
        )
      },
    })

    return uploadUrl.split('?')[0]
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles.map(({ file, errors }) => {
          if (errors[0]?.code === 'file-too-large') {
            return tr(TrKey.mediaFileTooLarge, {
              fileName: file.name,
              maxSize: parseSize(maxFileSize),
            })
          }
          return tr(TrKey.mediaInvalidFile, {
            fileName: file.name,
          })
        })
        showError(errors.join('. '))
        return
      }

      try {
        setStatus('uploading')
        setProgress(
          acceptedFiles.map((file) => ({
            name: file.name,
            percentage: 0,
          }))
        )

        const body = {
          files: acceptedFiles.map((file) => ({
            name: file.name,
            type: file.type,
          })),
          bucket,
        }
        const { err, data: presignedUrls } = await fetcher<
          {
            url: string
            uuid: string
            name: string
          }[]
        >({
          path: `files/upload-init`,
          method: 'POST',
          body,
        })
        if (err || !presignedUrls) throw err

        const uploadPromises = presignedUrls.map(({ url, name }) =>
          uploadToS3(url, acceptedFiles.find((file) => file.name === name)!)
        )
        await Promise.all(uploadPromises)

        await Promise.all(
          presignedUrls.map(({ uuid }) =>
            fetcher({
              path: `files/confirm-upload`,
              method: 'POST',
              body: { uuid },
            })
          )
        )

        success(tr(TrKey.mediaFilesUploaded))
        await onUploadSuccess(presignedUrls.map(({ uuid }) => uuid))
        setStatus('idle')
      } catch (error) {
        setStatus('error')
        showError(tr(TrKey.mediaUploadError))
        onUploadError?.(error as Error)
      }
    },
    [
      onUploadSuccess,
      onUploadError,
      success,
      showError,
      fetcher,
      tr,
      bucket,
      maxFileSize,
    ]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: maxFileSize,
  })

  return (
    <>
      <div
        {...getRootProps()}
        className={`p-8 mb-4 h-32 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors flex items-center justify-center
          ${
            isDragActive
              ? 'border-primary-400 bg-primary-400/10'
              : 'border-primary-400/50 hover:border-primary-400'
          }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>{tr(TrKey.mediaDropImages)}</p>
        ) : (
          <div className='space-y-2'>
            <p>{tr(TrKey.mediaDragNDrop)}</p>
            <p className='text-sm text-gray-600'>
              {tr(TrKey.mediaMaxFileSize, {
                maxSize: parseSize(maxFileSize),
              })}
            </p>
          </div>
        )}
      </div>

      {['uploading', 'error'].includes(status) && (
        <Modal
          title={tr(TrKey.mediaUploading)}
          className='max-w-3xl'
          onClose={() => setStatus('idle')}
        >
          <UploadProgress
            files={progress}
            status={status}
            onClose={() => setStatus('idle')}
          />
        </Modal>
      )}
    </>
  )
}

export default FileDropzone
